import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../parts/SvgIcon";
import BarGraph from "../../calendar/body-composition/components/BarGraph";
import AreaGraph from "../../calendar/body-composition/components/AreaGraph";
import moment from "moment";
import PropTypes from "prop-types";
import {
  determineAvg,
  mapBodyWeights,
} from "../../calendar/body-composition/body-composition.helper";
import submit from "submit-form";
import { parseTime } from "../../calendar/sleep-time/SleepTime";
import { round } from "lodash";
import convert from "convert-units";
import { getWaterIntakeAndUnit } from "../../../../utils/converters/convert-units";

const DailyGoals = ({ locals }) => {
  const { t } = useTranslation("athlete-portal");
  const { dailyStats, activeUser } = locals;
  const [filteredStats, setFilteredStats] = useState([]);
  const clientGoals = locals?.clientGoals;

  useEffect(() => {
    if (dailyStats.length > 0) {
      const utcReference = Number(
        moment().subtract(30, "days").format("YYYYMMDD")
      );

      const filtered = dailyStats.filter((stat) => stat.utcDate > utcReference);

      const sorted = [...filtered].sort((a, b) => b.utcDate - a.utcDate);

      setFilteredStats(sorted);
    }
  }, [dailyStats]);

  const stepsAverage = determineAvg(filteredStats, "steps");
  const sleepAverage = determineAvg(filteredStats, "sleep");
  const unitMultiplier = activeUser.units === "metric" ? 1 : 2.205;
  const unit = activeUser.units === "metric" ? "Kg" : "Lbs";
  const bodyweightData = filteredStats
    .map((data) => mapBodyWeights(data, unitMultiplier))
    .sort((a, b) => a.utcDate - b.utcDate);
  const lastBodyweight =
    filteredStats.length > 0
      ? filteredStats.filter((stat) => !!stat.bodyWeight)[0]
      : null;

  const utcReference = Number(moment().format("YYYYMMDD"));

  const sleepGoal = useMemo(() => {
    return (
      clientGoals.find(
        (goal) => goal.type === "sleep" && goal.utcStartDate <= utcReference
      ) || null
    );
  }, [clientGoals, utcReference]);

  const stepsGoal = useMemo(() => {
    return (
      clientGoals.find(
        (goal) => goal.type === "steps" && goal.utcStartDate <= utcReference
      ) || null
    );
  }, [clientGoals, utcReference]);

  const bodyWeightGoal = useMemo(() => {
    return (
      clientGoals.find(
        (goal) =>
          goal.type === "bodyWeight" &&
          goal.utcStartDate <= utcReference &&
          goal.utcTargetDate >= utcReference
      ) || null
    );
  }, [clientGoals, utcReference]);

  const waterIntakeGoal = useMemo(() => {
    return (
      clientGoals.find(
        (goal) =>
          goal.type === "waterIntake" && goal.utcStartDate <= utcReference
      ) || null
    );
  }, [clientGoals, utcReference]);

  const waterIntakeStats = filteredStats.filter((s) => s.waterIntake != null);
  const waterIntakeAverage =
    waterIntakeStats.length > 0
      ? round(
          waterIntakeStats.reduce((a, s) => a + Number(s.waterIntake), 0) /
            waterIntakeStats.length,
          1
        )
      : 0;
  const formatWater = (liters) =>
    getWaterIntakeAndUnit(liters, activeUser.units) ?? "0 L";

  return (
    <div className="profile-graphs">
      <div
        className={`daily-stat-card ${stepsGoal ? "active" : ""}`}
        onClick={() => {
          if (stepsGoal) {
            submit("/actions/refresh?", {
              method: "GET",
              body: {
                "modal[type]": "daily-goal-modal",
                goalId: stepsGoal._id,
              },
            });
          }
        }}
      >
        {!!stepsGoal && (
          <div className="current-goal-icon">
            <SvgIcon src={"/img/new-icons/arrow-square-right.svg"} />
          </div>
        )}
        <div className="daily-stat-chart">
          <div className="daily-stat-chart-title">
            <SvgIcon src={"/img/new-icons/steps.svg"} />
            <p>{t("dailyGoals.dailySteps")}</p>
          </div>
          <div className="chart-container">
            <BarGraph
              data={filteredStats}
              targetValue={"steps"}
              graphId={"steps-graph-profile"}
              average={stepsAverage}
              noTooltip={true}
            />
          </div>
        </div>
        <div className="daily-stat-card-footer">
          {stepsGoal ? (
            <div>
              <div className="daily-stat-card-goal-average">
                <p>{stepsAverage || 0}</p>
                <p>{t("dailyGoals.outOfSteps", { count: stepsGoal.details?.targetSteps })}</p>
              </div>
              <p className="p5">{t("dailyGoals.thirtyDayAvg")}</p>
            </div>
          ) : (
            <div>
              <div className="daily-stat-card-goal-average">
                <p>{stepsAverage || 0}</p>
                <p>{t("stepCount.steps")}</p>
              </div>
              <p className="p5">{t("dailyGoals.thirtyDayAvg")}</p>
            </div>
          )}
          {!stepsGoal ? (
            <div>
              <button
                className="z-1"
                data-button-type="outline"
                onClick={() => {
                  submit("/actions/refresh?", {
                    method: "GET",
                    body: {
                      "modal[type]": "add-client-goal",
                      modalState: "steps",
                    },
                  });
                }}
              >
                {t("dailyGoals.setGoal")}
              </button>
            </div>
          ) : (
            <CheckGoalCompleted
              goal={stepsGoal}
              averageStat={stepsAverage}
              type={"targetSteps"}
            />
          )}
        </div>
      </div>
      <div
        className={`daily-stat-card ${sleepGoal ? "active" : ""}`}
        onClick={() => {
          if (sleepGoal) {
            submit("/actions/refresh?", {
              method: "GET",
              body: {
                "modal[type]": "daily-goal-modal",
                goalId: sleepGoal._id,
              },
            });
          }
        }}
      >
        {!!sleepGoal && (
          <div className="current-goal-icon">
            <SvgIcon src={"/img/new-icons/arrow-square-right.svg"} />
          </div>
        )}
        <div className="daily-stat-chart">
          <div className="daily-stat-chart-title">
            <SvgIcon src={"/img/new-icons/sleep.svg"} />
            <p>{t("dailyGoals.dailySleep")}</p>
          </div>
          <div className="chart-container">
            <BarGraph
              data={filteredStats}
              targetValue={"sleep"}
              graphId={"sleep-graph-profile"}
              average={sleepAverage}
              parseFn={parseTime}
              noTooltip={true}
            />
          </div>
        </div>
        <div className="daily-stat-card-footer">
          {sleepGoal ? (
            <div>
              <div className="daily-stat-card-goal-average">
                <p>{parseTime(sleepAverage || 0)}</p>
                <p>/{parseTime(sleepGoal.details?.targetSleep)}</p>
              </div>
              <p className="p5">{t("dailyGoals.thirtyDayAvg")}</p>
            </div>
          ) : (
            <div>
              <div className="daily-stat-card-goal-average">
                <p>{parseTime(sleepAverage || 0)}</p>
              </div>
              <p className="p5">{t("dailyGoals.thirtyDayAvg")}</p>
            </div>
          )}
          {!sleepGoal ? (
            <div>
              <button
                className="z-1"
                data-button-type="outline"
                onClick={() => {
                  submit("/actions/refresh?", {
                    method: "GET",
                    body: {
                      "modal[type]": "add-client-goal",
                      modalState: "sleep",
                    },
                  });
                }}
              >
                {t("dailyGoals.setGoal")}
              </button>
            </div>
          ) : (
            <CheckGoalCompleted
              goal={sleepGoal}
              averageStat={sleepAverage}
              type={"targetSleep"}
            />
          )}
        </div>
      </div>
      <div
        className={`daily-stat-card ${waterIntakeGoal ? "active" : ""}`}
        onClick={() => {
          if (waterIntakeGoal) {
            submit("/actions/refresh?", {
              method: "GET",
              body: {
                "modal[type]": "daily-goal-modal",
                goalId: waterIntakeGoal._id,
              },
            });
          }
        }}
      >
        {!!waterIntakeGoal && (
          <div className="current-goal-icon">
            <SvgIcon src={"/img/new-icons/arrow-square-right.svg"} />
          </div>
        )}
        <div className="daily-stat-chart">
          <div className="daily-stat-chart-title">
            <SvgIcon src={"/img/new-icons/drop-icon.svg"} />
            <p>{t("dailyGoals.waterIntake")}</p>
          </div>
          <div className="chart-container">
            <BarGraph
              data={filteredStats}
              targetValue={"waterIntake"}
              graphId={"water-intake-graph-profile"}
              average={waterIntakeAverage}
              noTooltip={true}
            />
          </div>
        </div>
        <div className="daily-stat-card-footer">
          {waterIntakeGoal ? (
            <div>
              <div className="daily-stat-card-goal-average">
                <p>{formatWater(waterIntakeAverage)}</p>
                <p>/{formatWater(waterIntakeGoal.details?.targetWaterIntake || 0)}</p>
              </div>
              <p className="p5">{t("dailyGoals.thirtyDayAvg")}</p>
            </div>
          ) : (
            <div>
              <div className="daily-stat-card-goal-average">
                <p>{formatWater(waterIntakeAverage)}</p>
              </div>
              <p className="p5">{t("dailyGoals.thirtyDayAvg")}</p>
            </div>
          )}
          {!waterIntakeGoal ? (
            <div>
              <button
                className="z-1"
                data-button-type="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  submit("/actions/refresh?", {
                    method: "GET",
                    body: {
                      "modal[type]": "add-client-goal",
                      modalState: "waterIntake",
                    },
                  });
                }}
              >
                {t("dailyGoals.setGoal")}
              </button>
            </div>
          ) : (
            <CheckGoalCompleted
              goal={waterIntakeGoal}
              averageStat={waterIntakeAverage}
              type={"targetWaterIntake"}
            />
          )}
        </div>
      </div>
      <div
        className={`daily-stat-card ${bodyWeightGoal ? "active" : ""}`}
        onClick={() => {
          if (bodyWeightGoal) {
            submit("/actions/refresh?", {
              method: "GET",
              body: {
                "modal[type]": "daily-goal-modal",
                goalId: bodyWeightGoal._id,
              },
            });
          }
        }}
      >
        {!!bodyWeightGoal && (
          <div className="current-goal-icon">
            <SvgIcon src={"/img/new-icons/arrow-square-right.svg"} />
          </div>
        )}
        <div className="daily-stat-chart">
          <div className="daily-stat-chart-title">
            <SvgIcon src={"/img/new-icons/bodyweight.svg"} />
            <p>{t("metrics.bodyweight")}</p>
          </div>
          <div className="chart-container">
            <AreaGraph
              data={bodyweightData}
              targetValue={"bodyWeight"}
              graphId={"bodyweight-graph-profile"}
              label={"Body Weight"}
              description={"Weight"}
              units={unit}
              noTooltip={true}
            />
          </div>
        </div>
        <div className="daily-stat-card-footer">
          {bodyWeightGoal ? (
            <div>
              <div className="daily-stat-card-goal-average">
                <p>
                  {activeUser.units === "metric"
                    ? round(lastBodyweight?.bodyWeight || 0, 2)
                    : round(
                        convert(lastBodyweight?.bodyWeight || 0)
                          .from("kg")
                          .to("lb"),
                        2
                      )}
                </p>
                <p>
                  /
                  {activeUser.units === "metric"
                    ? round(bodyWeightGoal?.details?.targetWeight, 2)
                    : round(
                        convert(bodyWeightGoal?.details?.targetWeight)
                          .from("kg")
                          .to("lb"),
                        2
                      )}{" "}
                  {unit}
                </p>
              </div>
              <p className="p5">
                {t("dailyGoals.per")}{" "}
                {moment(
                  lastBodyweight?.utcDate || moment().format("YYYYMMDD"),
                  "YYYYMMDD"
                ).format("MMM D, YYYY")}
              </p>
            </div>
          ) : (
            <div>
              <div className="daily-stat-card-goal-average">
                <p>
                  {activeUser.units === "metric"
                    ? round(lastBodyweight?.bodyWeight || 0, 2)
                    : round(
                        convert(lastBodyweight?.bodyWeight || 0)
                          .from("kg")
                          .to("lb"),
                        2
                      )}{" "}
                  {unit}
                </p>
              </div>
              <p className="p5">
                {t("dailyGoals.per")}{" "}
                {moment(
                  lastBodyweight?.utcDate || moment().format("YYYYMMDD"),
                  "YYYYMMDD"
                ).format("MMM D, YYYY")}
              </p>
            </div>
          )}
          {!bodyWeightGoal ? (
            <div>
              <button
                className="z-1"
                data-button-type="outline"
                onClick={() => {
                  submit("/actions/refresh?", {
                    method: "GET",
                    body: {
                      "modal[type]": "add-client-goal",
                      modalState: "bodyWeight",
                    },
                  });
                }}
              >
                {t("dailyGoals.setGoal")}
              </button>
            </div>
          ) : (
            <div className="bodyweight-target">
              <p>
                {t("dailyGoals.targetLabel")}{" "}
                {activeUser.units === "metric"
                  ? round(bodyWeightGoal?.details?.targetWeight, 2)
                  : round(
                      convert(bodyWeightGoal?.details?.targetWeight)
                        .from("kg")
                        .to("lb"),
                      2
                    )}{" "}
                {unit}
              </p>
              <p className="p5">
                {t("dailyGoals.by")} {moment(bodyWeightGoal.targetDate).format("MMM D, YYYY")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CheckGoalCompleted = ({ goal, type, averageStat }) => {
  if (averageStat >= goal.details?.[type]) {
    return <div className="chip success"></div>;
  } else {
    return <div className="chip warning"></div>;
  }
};

DailyGoals.propTypes = {
  locals: PropTypes.any,
};

CheckGoalCompleted.propTypes = {
  goal: PropTypes.any,
  type: PropTypes.any,
  averageStat: PropTypes.any,
};

export default DailyGoals;
